import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  width = 'w-full', 
  height = 'h-4' 
}) => {
  return (
    <div 
      className={`animate-pulse bg-gray-200 rounded ${width} ${height} ${className}`}
      aria-label="読み込み中"
    />
  );
};

export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
      <div className="animate-pulse">
        <div className="flex items-center space-x-4 mb-4">
          <Skeleton width="w-12" height="h-12" className="rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton width="w-3/4" height="h-4" />
            <Skeleton width="w-1/2" height="h-3" />
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton width="w-full" height="h-3" />
          <Skeleton width="w-5/6" height="h-3" />
          <Skeleton width="w-4/6" height="h-3" />
        </div>
      </div>
    </div>
  );
};

export const SkeletonTable: React.FC<{ 
  rows?: number; 
  columns?: number; 
  className?: string 
}> = ({ 
  rows = 5, 
  columns = 4, 
  className = '' 
}) => {
  return (
    <div className={`bg-white shadow rounded-lg overflow-hidden ${className}`}>
      <div className="animate-pulse">
        {/* ヘッダー */}
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, index) => (
              <Skeleton key={index} width="w-20" height="h-4" />
            ))}
          </div>
        </div>
        
        {/* 行 */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4 border-b border-gray-200 last:border-b-0">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton key={colIndex} width="w-full" height="h-4" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SkeletonList: React.FC<{ 
  items?: number; 
  className?: string 
}> = ({ 
  items = 5, 
  className = '' 
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="bg-white shadow rounded-lg p-4">
          <div className="animate-pulse flex items-center space-x-4">
            <Skeleton width="w-10" height="h-10" className="rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton width="w-3/4" height="h-4" />
              <Skeleton width="w-1/2" height="h-3" />
            </div>
            <Skeleton width="w-16" height="h-8" className="rounded" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const SkeletonForm: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
      <div className="animate-pulse space-y-6">
        <div className="space-y-2">
          <Skeleton width="w-20" height="h-4" />
          <Skeleton width="w-full" height="h-10" className="rounded" />
        </div>
        <div className="space-y-2">
          <Skeleton width="w-24" height="h-4" />
          <Skeleton width="w-full" height="h-10" className="rounded" />
        </div>
        <div className="space-y-2">
          <Skeleton width="w-16" height="h-4" />
          <Skeleton width="w-full" height="h-24" className="rounded" />
        </div>
        <div className="flex justify-end space-x-3">
          <Skeleton width="w-20" height="h-10" className="rounded" />
          <Skeleton width="w-24" height="h-10" className="rounded" />
        </div>
      </div>
    </div>
  );
};